// ============================================================================
// INVITE INVALID SCREEN
// Phase 2A Step 6: Error screen for invalid/expired invites
// Shows clear error message (NO dashboard fallback)
// ============================================================================

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, XCircle } from "lucide-react";
import { clearInviteToken } from "@/lib/inviteTokenStorage";

interface InviteInvalidScreenProps {
  reason?: string;
}

/**
 * Error screen shown when invite is invalid/expired
 * NO dashboard fallback - user must use a valid invite
 */
export const InviteInvalidScreen = ({ reason }: InviteInvalidScreenProps) => {
  const navigate = useNavigate();

  const handleClearAndRetry = () => {
    clearInviteToken();
    navigate("/login", { replace: true });
  };

  const getReasonMessage = () => {
    switch (reason) {
      case "expired":
        return "This invite link has expired. Please request a new invite from the property owner.";
      case "already_accepted":
        return "This invite has already been used. Please request a new invite if you need access.";
      case "email_mismatch":
        return "This invite is for a different email address. Please use the email address the invite was sent to.";
      case "token_not_found":
        return "This invite link is invalid. Please check the link or request a new invite.";
      case "missing_task_or_property":
        return "This invite is incomplete. Please request a new invite from the property owner.";
      case "task_not_found":
        return "The task associated with this invite no longer exists. Please request a new invite.";
      case "acceptance_failed":
        return "There was an error processing this invite. Please try again or request a new invite.";
      default:
        return "This invite link is invalid or has expired. Please request a new invite from the property owner.";
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            <CardTitle>Invalid Invite</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">{getReasonMessage()}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClearAndRetry}
              className="flex-1"
            >
              Go to Login
            </Button>
            <Button
              onClick={() => window.location.reload()}
              className="flex-1"
            >
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

